import React, { useEffect, useState } from "react";
import {
  Container, Typography, Box, Paper, TextField, Button,
  Grid, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton
} from "@mui/material";
import {
  collection, doc, getDoc, getDocs, setDoc,
  updateDoc, increment, Timestamp, arrayUnion
} from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";

const IRAQ_CITIES = [
  "بغداد", "أربيل", "البصرة", "الموصل", "النجف", "كربلاء", "ذي قار", 
  "كركوك", "الأنبار", "ديالى", "المثنى", "القادسية", "ميسان", "واسط", 
  "صلاح الدين", "دهوك", "السليمانية", "بابل"
];

// قائمة الشركاء حسب المدينة (يمكنك إضافة شركاء جدد هنا)
const PARTNERS_LIST = {
  "بغداد": ["شريك بغداد 1", "شريك بغداد 2"],
  "أربيل": ["الريف كافيه", "شريك أربيل 2"],
  "البصرة": ["شريك البصرة 1"]
};

const SIZES = [
  "30x40", "40x60", "50x70", "60x80", "70x90", "70x100", "80x120",
  "120x180", "160x240", "30x30", "40x40", "50x50", "60x60", "70x70",
  "80x80", "90x90", "100x100", "110x110", "120x120", "130x130",
];

const PartnerInventory = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCity, setFilterCity] = useState("all");

  // States الإدخال
  const [city, setCity] = useState("");
  const [partner, setPartner] = useState(""); // حقل الشريك الجديد
  const [category, setCategory] = useState("");
  const [material, setMaterial] = useState("");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");

  const rawUser = localStorage.getItem("username");
  const isAdmin = rawUser === "admin";

  useEffect(() => { fetchInventory(); }, []);

  const fetchInventory = async () => {
    const snap = await getDocs(collection(db, "partnerInventory"));
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const sortedData = data.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
    setInventory(sortedData);
  };

  const resetFields = () => {
    setPartner(""); setQty(""); setCategory(""); setMaterial(""); setSize(""); setPrice("");
  };

  const handleSaveNew = async () => {
    if (!city || !category || !qty || (PARTNERS_LIST[city] && !partner)) {
      return alert("⚠️ أكمل الحقول الأساسية واختيار الشريك");
    }
    const note = window.prompt("📝 سبب الإضافة للمخزن (إجباري):");
    if (!note) return alert("❌ يجب ذكر السبب");

    const enteredQty = Number(qty);
    // تكوين ID فريد للشركاء
    let finalId = `${city}_${partner || 'NoPartner'}_${category}_${material}_${size}`;

    const ref = doc(db, "partnerInventory", finalId);
    let logEntry = { 
      type: "إضافة جديد", 
      quantity: enteredQty, 
      date: new Date().toLocaleString("en-US"), 
      reason: note 
    };

    let dataToSave = {
      city, partner, category, material, size,
      quantity: increment(enteredQty),
      updatedAt: Timestamp.now(),
      logs: arrayUnion(logEntry)
    };
    if (isAdmin && price) dataToSave.price = Number(price);

    await setDoc(ref, dataToSave, { merge: true });
    alert("✅ تم تحديث مخزن الشركاء");
    resetFields();
    fetchInventory();
  };

  const handleQuickUpdate = async (item, type) => {
    const val = window.prompt(type === "add" ? "كمية الإضافة:" : "كمية الخصم:", "1");
    if (!val || isNaN(val) || Number(val) <= 0) return;
    const note = window.prompt("📝 ما هو السبب؟ (إجباري):");
    if (!note) return alert("❌ السبب مطلوب!");

    const change = Number(val);
    if (type === "remove" && change > item.quantity) return alert("❌ الكمية غير كافية");

    const ref = doc(db, "partnerInventory", item.id);
    await updateDoc(ref, {
      quantity: increment(type === "add" ? change : -change),
      updatedAt: Timestamp.now(),
      logs: arrayUnion({ type: type === "add" ? "زيادة" : "خصم", quantity: change, date: new Date().toLocaleString("en-US"), reason: note })
    });
    fetchInventory();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }} dir="rtl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={900} color="secondary">🤝 مخزن الشركاء (Artika Partners)</Typography>
        <Button variant="outlined" color="secondary" onClick={() => navigate("/baneen")}>رجوع ⬅</Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 4, borderTop: '6px solid #9c27b0' }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>إضافة مواد لشريك</Typography>
            
            {/* 1. المدينة */}
            <TextField select fullWidth label="المحافظة" value={city} onChange={(e) => { setCity(e.target.value); setPartner(""); }} sx={{ mb: 2 }}>
              {IRAQ_CITIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>

            {/* 2. الشريك (يظهر فقط إذا كانت المحافظة تحتوي على شركاء) */}
            {city && PARTNERS_LIST[city] && (
              <TextField select fullWidth label="اسم الشريك" value={partner} onChange={(e) => setPartner(e.target.value)} sx={{ mb: 2 }}>
                {PARTNERS_LIST[city].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            )}

            {/* 3. نوع المادة */}
            {city && (
              <TextField select fullWidth label="نوع المادة" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ mb: 2 }}>
                <MenuItem value="تابليت">تابليت</MenuItem>
                <MenuItem value="اكرلك">اكرلك</MenuItem>
                <MenuItem value="انارة">انارة</MenuItem>
                <MenuItem value="ستاند حديد">ستاند حديد</MenuItem>
                <MenuItem value="لوحة - اورجن">لوحة - اورجن</MenuItem>
                <MenuItem value="لوحة - ادشن">لوحة - ادشن</MenuItem>
                <MenuItem value="لوحة - برايم">لوحة - برايم</MenuItem>
                <MenuItem value="فوم">فوم</MenuItem>
                <MenuItem value="من نحن">بوستر "من نحن"</MenuItem>
              </TextField>
            )}

            {/* 4. التفاصيل والقياس */}
            {category && category.includes("لوحة") && (
              <TextField select fullWidth label="القياس" value={size} onChange={(e) => setSize(e.target.value)} sx={{ mb: 2 }}>
                {SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            )}

            {category && (
              <>
                <TextField fullWidth type="number" label="الكمية / العدد" value={qty} onChange={(e) => setQty(e.target.value)} sx={{ mb: 2 }} />
                {isAdmin && <TextField fullWidth label="السعر الكلي (إن وجد)" type="number" value={price} onChange={(e) => setPrice(e.target.value)} sx={{ mb: 3 }} />}
                <Button fullWidth variant="contained" color="secondary" size="large" onClick={handleSaveNew} sx={{ borderRadius: 3, fontWeight: 'bold' }}>💾 حفظ في مخزن الشريك</Button>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: '1px solid #eee', bgcolor: '#fafafa' }}>
            <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
              <Typography variant="h6" fontWeight={800}>📊 جرد مواد الشركاء</Typography>
              <TextField size="small" placeholder="بحث عن شريك أو مدينة..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ width: 250, bgcolor: 'white' }} />
            </Box>

            <TableContainer sx={{ maxHeight: 650, borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="right" sx={{ bgcolor: '#9c27b0', color: 'white', fontWeight: 'bold' }}>الموقع / الشريك</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#9c27b0', color: 'white', fontWeight: 'bold' }}>المادة</TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#9c27b0', color: 'white', fontWeight: 'bold' }}>الكمية</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#9c27b0', color: 'white', fontWeight: 'bold' }}>آخر تحديث</TableCell>
                    {isAdmin && <TableCell align="center" sx={{ bgcolor: '#9c27b0', color: 'white', fontWeight: 'bold' }}>📜</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory
                    .filter(i => i.city.includes(searchQuery) || i.partner.includes(searchQuery) || i.category.includes(searchQuery))
                    .map((item) => (
                      <TableRow key={item.id} hover sx={{ bgcolor: 'white' }}>
                        <TableCell align="right">
                          <Chip label={item.city} size="small" sx={{ mb: 0.5 }} /><br/>
                          <Typography variant="body2" fontWeight="bold" color="secondary">{item.partner || "بدون اسم"}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">{item.category}</Typography>
                          <Typography variant="caption" color="textSecondary">{item.size} {item.material}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" sx={{ bgcolor: '#f3e5f5', borderRadius: '20px', p: '2px' }}>
                            <IconButton size="small" color="error" onClick={() => handleQuickUpdate(item, "remove")}>➖</IconButton>
                            <Typography variant="body2" fontWeight="900" sx={{ mx: 1 }}>{item.quantity}</Typography>
                            <IconButton size="small" color="success" onClick={() => handleQuickUpdate(item, "add")}>➕</IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: '10px', color: '#888' }}>
                          {item.updatedAt?.toDate ? item.updatedAt.toDate().toLocaleString('en-US', { dateStyle: 'short' }) : "-"}
                        </TableCell>
                        {isAdmin && (
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => alert(item.logs?.map(l => `[${l.date}] ${l.type}: ${l.quantity}\nالسبب: ${l.reason}`).join('\n\n') || "لا يوجد سجل")}>
                              📜
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PartnerInventory;